import { AxiosResponse } from 'axios'
import { Action } from 'redux'
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeLatest,
} from 'redux-saga/effects'
import { getNestedProperty } from '@opallabs/lambda'
import { fetchWorkflow } from '../../actions/fetchWorkflowByContext'
import { fetchMoment, include, fields } from '../../actions/getMoment'
import { loadJsonApiEntities } from '../../actions/loadJsonApiEntities'
import addViewToStore from '../../actions/views/addViewToStore'
import { WorkflowResponsePayload } from '../../actions/workflow/types'
import { workflowActions } from '../../actions/workflowActions'
import workflowError from '../../actions/workflowError'

type Yield = CallEffect | SelectEffect | PutEffect
type Return = void
type Next = AxiosResponse<WorkflowResponsePayload> | undefined

type Payload = {
  view: string
  params: {
    id: string
    entity: 'moment' | 'story'
    expose: boolean
  }
  callback?: () => void
}

export interface ActionWithPayload extends Action {
  payload: Payload
}

export function* fetchWorkflowByContext(
  action: ActionWithPayload
): Generator<Yield, Return, Next> {
  try {
    const view = yield select(
      (state) =>
        state.entityDataByView &&
        action.payload.view &&
        state.entityDataByView[action.payload.view]
    )

    if (view === undefined) {
      yield put(addViewToStore({ view: action.payload.view }))
    }

    let workflow

    if (action.payload.params.entity === 'moment') {
      workflow = yield call(fetchWorkflow, action.payload.params)
    }

    if (workflow !== undefined) {
      yield put(loadJsonApiEntities(workflow.data))
    }

    // Fetch entity object (moment, story) if not already
    // loaded in Redux store
    let entityObject = yield select((state) =>
      getNestedProperty(
        state,
        'jsonApiEntities',
        action.payload.params.entity,
        action.payload.params.id
      )
    )
    let entityFetched = false

    if (entityObject === undefined) {
      if (action.payload.params.entity === 'moment') {
        entityFetched = true
        entityObject = yield call(fetchMoment, {
          momentId: action.payload.params.id,
          exposeUnscheduledMoments: true,
          include,
          fields,
        })
      }
    }

    if (entityFetched === true && entityObject !== undefined) {
      yield put(loadJsonApiEntities(entityObject.data))
    }

    if (action.payload.callback) {
      action.payload.callback()
    }
  } catch (error) {
    console.error(error)

    yield put(
      workflowError({
        context: 'workflow',
        entity: action.payload.params.entity,
        error: error as Error,
        requestType: 'read',
        loading: { workflow: false },
      })
    )
  }
}

export function* fetchWorkflowByContextSaga(): Generator<
  ForkEffect,
  void,
  void
> {
  yield takeLatest(
    workflowActions.FETCH_WORKFLOW_BY_CONTEXT,
    fetchWorkflowByContext
  )
}

export default [fetchWorkflowByContextSaga]
